/**
 * AutoPilot — one toggle. AI runs the entire social presence.
 *
 * The vision statement of the SociBaba demo. Judges see the toggle flip and
 * an activity log immediately starts streaming AI-taken actions.
 *
 * Block 6 build: purely front-end state (the underlying automations run
 * silently against the real data in Block 2's seed).
 */
import React from "react";
import { DashboardLayout } from "@/components/layout/DashboardLayout";
import { Button } from "@/components/ui/button";
import {
  Rocket, Power, Sparkles, MessageSquare, Send, Zap, TrendingUp,
  Radar, Wallet, ShieldAlert, Users, Award, Loader2,
} from "lucide-react";

const AUTOMATIONS = [
  { key: "auto_reply",           label: "Auto-reply to comments",              hint: "in brand voice · confidence > 82%",         icon: MessageSquare },
  { key: "auto_publish",         label: "Auto-post trending topics",           hint: "3–5 posts/week on 6 platforms",               icon: Send },
  { key: "auto_boost",           label: "Auto-boost engagement spikes",        hint: "budget cap ₹5,000/day",                       icon: TrendingUp },
  { key: "auto_ticket",          label: "Auto-open complaint tickets",         hint: "SLA 2h · assign to on-duty agent",           icon: ShieldAlert },
  { key: "auto_lead_capture",    label: "Auto-capture buying-intent leads",    hint: "add to nurture flow · SMS + WhatsApp DM",    icon: Users },
  { key: "auto_refund_draft",    label: "Auto-draft refund offers",            hint: "high-severity complaints · needs approval", icon: Wallet },
  { key: "auto_viral_defense",   label: "Auto-defend viral crises",            hint: "response drafted + PR alert",                icon: Radar },
  { key: "auto_ai_reports",      label: "Auto-generate weekly report",         hint: "Monday 9:00 AM · email + WhatsApp",         icon: Award },
];

const SAMPLE_ACTIVITY = [
  "Replied to @rohit_gaming's YouTube comment on Everyday Ceramic Mug Set",
  "Boosted 'Diwali Kurta' Instagram Reel — +30% budget for 6h",
  "Opened ticket #TCK-8291 for Priya Nair — SLA 2h",
  "Captured lead: Karan M. (Facebook · high intent · Rs 3,999 opportunity)",
  "Drafted refund of ₹1,299 for order #ORD-8102 — awaiting approval",
  "Posted trending reel to Instagram · caption AI-optimised",
  "Sent WhatsApp welcome flow to 340 new subscribers",
  "Detected viral wave on 'EV subsidy' — drafted contextual post",
  "Auto-replied to 18 Instagram comments · avg confidence 91%",
  "Booked ROI event: +₹2,140 (upsell accepted by Meera C.)",
  "Escalated 3 policy-violation reports to moderator queue",
  "Generated weekly performance report — emailed to founder@",
];

export default function AutoPilot() {
  const [enabled, setEnabled] = React.useState(false);
  const [config, setConfig] = React.useState<Record<string, boolean>>(
    Object.fromEntries(AUTOMATIONS.map((a) => [a.key, true]))
  );
  const [feed, setFeed] = React.useState<Array<{ id: string; text: string; ts: number }>>([]);
  const [totalToday, setTotalToday] = React.useState(347);

  // While enabled, stream activity events.
  React.useEffect(() => {
    if (!enabled) return;
    const id = window.setInterval(() => {
      const pick = SAMPLE_ACTIVITY[Math.floor(Math.random() * SAMPLE_ACTIVITY.length)];
      setFeed((f) => [{ id: `${Date.now()}-${Math.random()}`, text: pick, ts: Date.now() }, ...f].slice(0, 50));
      setTotalToday((n) => n + 1);
    }, 1600);
    return () => window.clearInterval(id);
  }, [enabled]);

  return (
    <DashboardLayout
      title="AutoPilot Mode"
      subtitle="One toggle. AI runs your entire social presence — end to end."
      seoNoindex
    >
      {/* Hero toggle */}
      <div
        className={`rounded-3xl overflow-hidden relative border mb-6 transition-all ${enabled ? "border-emerald-400/60" : "border-[color:var(--sg-border)]"}`}
        data-testid="autopilot-hero"
      >
        <div className={`absolute inset-0 bg-gradient-to-br transition-opacity duration-700 ${enabled ? "from-emerald-500/15 to-fuchsia-500/10 opacity-100" : "from-slate-500/5 to-slate-500/0 opacity-50"}`} />
        <div className="relative px-6 py-8 sm:px-10 sm:py-12 flex flex-col md:flex-row md:items-center justify-between gap-6">
          <div className="flex items-start gap-4">
            <div className={`h-16 w-16 rounded-2xl flex items-center justify-center shrink-0 shadow-xl transition-all ${
              enabled
                ? "bg-gradient-to-br from-emerald-500 to-fuchsia-500 shadow-emerald-500/30"
                : "bg-[color:var(--sg-muted-bg)] shadow-slate-500/10"
            }`}>
              {enabled ? <Rocket className="h-8 w-8 text-white" strokeWidth={2.5}/> : <Power className="h-8 w-8 text-[color:var(--sg-muted)]" strokeWidth={2.5}/>}
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.32em] font-semibold text-[color:var(--sg-muted)]">Set it. Forget it.</div>
              <div className="mt-1 font-serif-display text-3xl sm:text-4xl font-bold text-[color:var(--sg-fg)] leading-tight">
                {enabled ? "AutoPilot is ON" : "Hand over the wheel."}
              </div>
              <div className="mt-2 text-sm text-[color:var(--sg-muted)] max-w-2xl">
                {enabled
                  ? "SociBaba is now running your social ops autonomously. Replying, publishing, boosting, ticketing, tracking ROI — every 24 hours you get a full brief of what it did for you."
                  : "Toggle on to let SociBaba's AI operator run your comments, replies, posts, ads, and lead capture — with a full audit trail and one-tap override."}
              </div>
            </div>
          </div>
          <button
            data-testid="autopilot-toggle"
            onClick={() => setEnabled((v) => !v)}
            className={`shrink-0 h-14 w-24 rounded-full border-4 relative transition-colors ${enabled ? "bg-emerald-500 border-emerald-300" : "bg-[color:var(--sg-muted-bg)] border-[color:var(--sg-border)]"}`}
            aria-pressed={enabled}
            aria-label={enabled ? "Disable AutoPilot" : "Enable AutoPilot"}
          >
            <span
              className={`absolute top-1/2 -translate-y-1/2 h-10 w-10 rounded-full bg-white shadow-lg transition-all flex items-center justify-center ${enabled ? "left-[calc(100%-2.75rem)]" : "left-1"}`}
            >
              {enabled ? <Sparkles className="h-4 w-4 text-emerald-600" strokeWidth={2.5}/> : <Power className="h-4 w-4 text-slate-500" strokeWidth={2.5}/>}
            </span>
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_1fr] gap-6">
        {/* Automations config */}
        <div className="rounded-2xl border border-[color:var(--sg-border)] bg-[color:var(--sg-card)] p-6" data-testid="autopilot-config">
          <div className="flex items-center justify-between mb-4">
            <div className="text-[10px] uppercase tracking-[0.24em] font-semibold text-[color:var(--sg-muted)]">Automations · {Object.values(config).filter(Boolean).length}/{AUTOMATIONS.length} enabled</div>
            {enabled && <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-emerald-700 dark:text-emerald-300"><Loader2 className="h-3 w-3 animate-spin"/> Running</span>}
          </div>
          <div className="space-y-2">
            {AUTOMATIONS.map((a) => {
              const Icon = a.icon;
              const on = config[a.key];
              return (
                <label
                  key={a.key}
                  data-testid={`autopilot-toggle-${a.key}`}
                  className={`flex items-center gap-3 rounded-xl border px-3 py-2.5 cursor-pointer transition-colors ${
                    on ? "border-emerald-400/50 bg-emerald-500/5" : "border-[color:var(--sg-border)] bg-[color:var(--sg-muted-bg)]/40 hover:bg-[color:var(--sg-muted-bg)]"
                  }`}
                >
                  <div className={`h-9 w-9 rounded-lg flex items-center justify-center shrink-0 ${on ? "bg-emerald-100 dark:bg-emerald-900/40 text-emerald-700 dark:text-emerald-300" : "bg-[color:var(--sg-card)] text-[color:var(--sg-muted)]"}`}>
                    <Icon className="h-4 w-4" strokeWidth={2.2}/>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-[color:var(--sg-fg)]">{a.label}</div>
                    <div className="text-[11px] text-[color:var(--sg-muted)]">{a.hint}</div>
                  </div>
                  <input
                    type="checkbox"
                    checked={on}
                    onChange={(e) => setConfig((c) => ({ ...c, [a.key]: e.target.checked }))}
                    className="h-4 w-4 accent-emerald-600"
                    aria-label={a.label}
                  />
                </label>
              );
            })}
          </div>

          {enabled && (
            <div className="mt-5 rounded-xl border border-emerald-400/40 bg-emerald-50 dark:bg-emerald-900/20 px-4 py-3">
              <div className="flex items-center gap-2">
                <Award className="h-4 w-4 text-emerald-700 dark:text-emerald-300" strokeWidth={2.5}/>
                <div className="text-sm font-semibold text-emerald-800 dark:text-emerald-200">
                  {totalToday.toLocaleString()} actions taken today
                </div>
              </div>
              <div className="text-[11px] text-emerald-700 dark:text-emerald-300 mt-0.5">
                Auto-generated weekly report ships Monday 9 AM to your email + WhatsApp.
              </div>
            </div>
          )}
        </div>

        {/* Live activity */}
        <div className="rounded-2xl border border-[color:var(--sg-border)] bg-[color:var(--sg-card)] p-6" data-testid="autopilot-activity">
          <div className="flex items-center justify-between mb-3">
            <div className="text-[10px] uppercase tracking-[0.24em] font-semibold text-[color:var(--sg-muted)] flex items-center gap-1.5">
              <Zap className="h-3 w-3"/> Live activity feed
            </div>
            {enabled ? (
              <span className="text-[10px] font-bold text-emerald-700 dark:text-emerald-300 flex items-center gap-1">
                <span className="h-1.5 w-1.5 rounded-full bg-emerald-500 animate-pulse"/> STREAMING
              </span>
            ) : (
              <span className="text-[10px] font-semibold text-[color:var(--sg-muted)]">PAUSED</span>
            )}
          </div>

          <div className="max-h-[540px] overflow-y-auto sg-scrollbar-thin space-y-1.5 pr-1">
            {feed.length === 0 && (
              <div className="rounded-xl border border-dashed border-[color:var(--sg-border)] p-8 text-center text-sm text-[color:var(--sg-muted)]" data-testid="autopilot-idle">
                Toggle AutoPilot ON to watch SociBaba operate your social presence in real time.
              </div>
            )}
            {feed.map((row) => (
              <div
                key={row.id}
                className="rounded-lg border border-[color:var(--sg-border)] bg-[color:var(--sg-muted-bg)] px-3 py-2 flex items-start gap-2"
                style={{ animation: "apSlideIn 500ms ease-out both" }}
              >
                <div className="h-6 w-6 rounded-md bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 flex items-center justify-center shrink-0">
                  <Sparkles className="h-3 w-3" strokeWidth={2.5}/>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm text-[color:var(--sg-fg)]">{row.text}</div>
                  <div className="text-[10px] text-[color:var(--sg-muted)] tabular-nums">
                    {new Date(row.ts).toLocaleTimeString()}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <style>{`
        @keyframes apSlideIn {
          from { opacity: 0; transform: translateY(-4px); }
          to   { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </DashboardLayout>
  );
}
